import { medRequestStatusValueToLabel } from '@/constants/statuses'
import { colors } from '@/theme'
import type { MedicationRequest } from '@/services/med-req-detail'
import { DocumentScanner } from '@mui/icons-material'
import { Box, Chip, Paper, Typography } from '@mui/material'

interface HeaderProps {
  medRequest: MedicationRequest
}

// Status chip color mapping
const getStatusChipStyle = (status: string) => {
  const statusLower = status.toLowerCase()
  if (statusLower.includes('rejected') || statusLower.includes('cancelled')) {
    return { bgcolor: colors.status.errorLight, color: colors.status.error, borderColor: colors.status.error }
  }
  if (statusLower.includes('approved') || statusLower.includes('completed')) {
    return { bgcolor: colors.status.successLight, color: colors.status.success, borderColor: colors.status.success }
  }
  if (statusLower.includes('hold') || statusLower.includes('pending')) {
    return { bgcolor: colors.status.warningLight, color: colors.status.warning, borderColor: colors.status.warning }
  }
  return { bgcolor: colors.grey[100], color: colors.text.secondary, borderColor: colors.border.default }
}

export default function MedRequestHeader({ medRequest }: HeaderProps) {
  const stageLabel = medRequestStatusValueToLabel(medRequest.stage)
  const statusStyle = getStatusChipStyle(stageLabel)
  // const stageValue = medRequest.stage.toLowerCase()
  // const isRejected = stageValue.includes('rejected')
  const reasonText = medRequest.reasonForRejection || medRequest.reasonForApproval || medRequest.reasonForHold
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Main Banner */}
      <Paper
        sx={{
          py: 2,
          px: 4,
          bgcolor: colors.background.default,
          border: `1px solid ${colors.border.default}`,
          borderRadius: '16px',
          boxShadow: 'none'
        }}
      >
        {/* Order Info Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ color: colors.text.primary, fontSize: '16px', fontWeight: 600 }}>
            {medRequest.orderNumber} {medRequest.orderedItem.name} - {medRequest.orderedItem.dose},{' '}
            {medRequest.orderedItem.quantity}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={stageLabel}
              size='medium'
              sx={{
                ...statusStyle,
                border: `1px solid`,
                fontWeight: 500,
                fontSize: '12px',
                p: 2
              }}
            />
            {medRequest.orderProcessType && (
              <Chip
                label={medRequest.orderProcessType}
                size='medium'
                variant='outlined'
                sx={{
                  borderColor: colors.border.default,
                  color: colors.text.secondary,
                  fontWeight: 500,
                  fontSize: '12px',
                  p: 2
                }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: 'column',
            p: 2,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: `1px solid ${colors.border.default}`
          }}
        >
          {/* Prescriber/Date Info */}
          {/* <Box sx={{ display: 'flex', gap: 4 }}>
            {medRequest.prescriber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ fontSize: '18px', color: colors.text.primary }} />
                <Typography variant='smallText' sx={{ color: colors.text.primary }}>
                  <strong>{isRejected ? 'Rejected by' : 'Approved by'}:</strong>{' '}
                  {medRequest.prescriber.fullName || '-'}
                </Typography>
              </Box>
            )}
            <Divider orientation='vertical' flexItem />
            {medRequest.prescriptionIssueDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth sx={{ fontSize: '18px', color: colors.text.primary }} />
                <Typography variant='smallText' sx={{ color: colors.text.primary }}>
                  <strong>{isRejected ? 'Rejected at' : 'Approved at'}:</strong> {medRequest.prescriptionIssueDate}
                </Typography>
              </Box>
            )}
          </Box>
          <Divider /> */}
          {/* Reason (priority: rejection > approval > hold) */}
          {reasonText && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DocumentScanner sx={{ fontSize: '18px', color: colors.text.primary, mt: 0.25 }} />
              <Typography variant='smallText' sx={{ color: colors.text.primary }}>
                <strong>Reason:</strong> {reasonText}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
